/**
 * Static evaluation, from Red's perspective (positive = Red is better).
 *
 * Material + piece-square tables + light king-safety. The PSTs follow standard
 * xiangqi principles (advance and centralize pawns after the river, keep horses
 * and cannons central, activate rooks, keep the king home behind its guards).
 * Tables are written rank 0 (Red's home) first up to rank 9 (Black's home).
 */

import { Color, PieceType, type Position } from "@xq/rules";

export const PIECE_VALUE: Record<PieceType, number> = {
  [PieceType.King]: 0, // handled via mate scores
  [PieceType.Advisor]: 200,
  [PieceType.Elephant]: 200,
  [PieceType.Horse]: 400,
  [PieceType.Rook]: 900,
  [PieceType.Cannon]: 450,
  [PieceType.Pawn]: 100,
};

// prettier-ignore
const PAWN: number[] = [
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  2,0,4,0,6,0,4,0,2,
  4,0,6,0,8,0,6,0,4,
  14,16,20,24,26,24,20,16,14,
  18,22,26,30,34,30,26,22,18,
  24,28,34,40,44,40,34,28,24,
  20,24,30,36,40,36,30,24,20,
  16,18,22,26,28,26,22,18,16,
];

// prettier-ignore
const HORSE: number[] = [
  0,-2,2,2,0,2,2,-2,0,
  -2,2,4,6,4,6,4,2,-2,
  2,4,6,8,8,8,6,4,2,
  2,6,8,10,10,10,8,6,2,
  4,8,10,12,12,12,10,8,4,
  4,8,10,12,12,12,10,8,4,
  2,6,8,10,10,10,8,6,2,
  2,4,6,8,8,8,6,4,2,
  0,2,4,6,6,6,4,2,0,
  0,-2,2,2,0,2,2,-2,0,
];

// prettier-ignore
const CANNON: number[] = [
  0,0,2,4,4,4,2,0,0,
  0,2,4,6,6,6,4,2,0,
  2,4,6,8,8,8,6,4,2,
  2,4,6,8,8,8,6,4,2,
  2,4,6,8,8,8,6,4,2,
  2,4,6,8,8,8,6,4,2,
  2,4,6,8,8,8,6,4,2,
  0,2,4,6,6,6,4,2,0,
  0,0,2,4,4,4,2,0,0,
  0,0,0,2,2,2,0,0,0,
];

// prettier-ignore
const ROOK: number[] = [
  4,6,6,8,8,8,6,6,4,
  6,8,8,10,10,10,8,8,6,
  6,8,8,10,10,10,8,8,6,
  6,8,8,10,10,10,8,8,6,
  8,10,10,12,12,12,10,10,8,
  8,10,10,12,12,12,10,10,8,
  8,10,10,14,14,14,10,10,8,
  10,12,12,14,14,14,12,12,10,
  8,10,12,14,14,14,12,10,8,
  6,8,10,12,12,12,10,8,6,
];

// prettier-ignore
const KING: number[] = [
  0,0,0,6,8,6,0,0,0,
  0,0,0,2,4,2,0,0,0,
  0,0,0,-2,0,-2,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,
];

const ZERO: number[] = new Array<number>(90).fill(0);

const PST: Record<PieceType, number[]> = {
  [PieceType.King]: KING,
  [PieceType.Advisor]: ZERO,
  [PieceType.Elephant]: ZERO,
  [PieceType.Horse]: HORSE,
  [PieceType.Rook]: ROOK,
  [PieceType.Cannon]: CANNON,
  [PieceType.Pawn]: PAWN,
};

/** Mirror a square vertically (Red<->Black perspective): file kept, rank -> 9-rank. */
function flip(sq: number): number {
  const file = sq % 9;
  const rank = (sq / 9) | 0;
  return (9 - rank) * 9 + file;
}

/**
 * Evaluate a position from Red's perspective. Does NOT detect terminal states
 * (mate/stalemate) — the search handles those with mate scores.
 */
export function evaluate(pos: Position): number {
  let score = 0;
  let redAdv = 0,
    redEle = 0,
    blackAdv = 0,
    blackEle = 0;

  for (let sq = 0; sq < 90; sq++) {
    const p = pos.board[sq];
    if (!p) continue;
    const val = PIECE_VALUE[p.type];
    if (p.color === Color.Red) {
      score += val + PST[p.type][sq]!;
      if (p.type === PieceType.Advisor) redAdv++;
      else if (p.type === PieceType.Elephant) redEle++;
    } else {
      score -= val + PST[p.type][flip(sq)]!;
      if (p.type === PieceType.Advisor) blackAdv++;
      else if (p.type === PieceType.Elephant) blackEle++;
    }
  }

  // Defensive solidity: intact guards make a king safer.
  score += (redAdv - blackAdv) * 8 + (redEle - blackEle) * 6;

  return score;
}
